import React from 'react'
import { Helmet } from 'react-helmet'
import { Route, Routes, Navigate } from 'react-router-dom'

import { NotFound } from './components/NotFound/NotFound'
import Accounts from './pages/Accounts/Accounts'
import { HomePage } from './pages/Home'
import { AlertsPage } from './pages/Alerts'
import {
  Waste,
  WasteDetails,
  WasteEdit,
  WasteEventLog,
  WasteLineItemEdit
} from './pages/Waste'
import {
  Delivery,
  DeliveryDetails,
  DeliveryLineItemEdit,
  DeliveryEdit,
  DeliveryEventLog
} from './pages/Delivery'
import { MetersPage, ReadingsPage } from './pages/Utilities'
import { BIReport } from './pages/BIReports'
import AccountSettings from './pages/Settings/AccountSettings'
import { DestinationLandingPage, Login } from './pages/Core'
import { SignInOidc, FirstSignIn, SignOut } from './pages/Auth'

import * as ROUTES from './routes'
import { Main } from './components/Navigation'
import { AssigneeNoRecordsAssignedPage } from './pages/AssigneeNoRecordsAssigned'
import { ProjectSettings } from './pages/ProjectSettings'
import { Forbidden } from './components/Forbidden/Forbidden'
import { ALERT_TYPES } from './lib/constants/alertTypes'
import { LoginFailedPage } from './pages/Auth/LoginFailed'

import axios from 'portal/utils/axios-msal'

axios.interceptors.response.use(
  response => response,

  error => {
    const shouldRedirect = !error.config?.doNotRedirectOn403
    if (shouldRedirect && error.response?.status === 403) {
      // redirect to 403 page
      window.location = ROUTES.FORBIDDEN
    }
    return Promise.reject(error)
  }
)

export function App() {
  return (
    <Routes>
      <Route
        path={ROUTES.SIGNIN_REDIRECT}
        element={
          <>
            <Helmet defer={false}>
              <title>Please wait...</title>
            </Helmet>
            <Main.UnauthenticatedRoute
              checkAuth={false}
              component={SignInOidc}
            />
          </>
        }
      />
      <Route
        path={ROUTES.FIRST_SIGNIN}
        element={
          <>
            <Helmet defer={false}>
              <title>Welcome to Qflow</title>
            </Helmet>
            <Main.UnauthenticatedRoute
              checkAuth
              redirectOnAuthenticated
              component={FirstSignIn}
            />
          </>
        }
      />

      <Route
        path={ROUTES.LOGIN_FAILED}
        element={
          <>
            <Helmet defer={false}>
              <title>Login Failed</title>
            </Helmet>
            <Main.UnauthenticatedRoute
              checkAuth
              redirectOnAuthenticated
              component={LoginFailedPage}
            />
          </>
        }
      />

      <Route
        path={ROUTES.LOGIN_FAILED}
        element={
          <>
            <Helmet defer={false}>
              <title>Login Failed</title>
            </Helmet>
            <Main.UnauthenticatedRoute
              checkAuth
              redirectOnAuthenticated
              component={LoginFailedPage}
            />
          </>
        }
      />
      <Route
        path={ROUTES.SIGN_OUT}
        element={
          <>
            <Helmet defer={false}>
              <title>Please wait...</title>
            </Helmet>
            <Main.UnauthenticatedRoute checkAuth={false} component={SignOut} />
          </>
        }
      />

      {/* Notification Destination */}
      <Route
        path={ROUTES.RECEIVED_DESTINATION}
        element={
          <Main.UnauthenticatedRoute component={DestinationLandingPage} />
        }
      />

      {/* Home page */}
      <Route
        path={ROUTES.HOMEPAGE}
        element={
          <>
            <Helmet defer={false}>
              <title>Home</title>
            </Helmet>
            <Main.AuthenticatedRoute component={HomePage} />
          </>
        }
      />

      {/* Delivery */}
      <Route
        path={ROUTES.DELIVERY_ROOT}
        element={<Navigate to={'./table'} />}
      />
      <Route
        path={ROUTES.DELIVERY_EVENTS}
        element={
          <>
            <Helmet defer={false}>
              <title>Delivery Events</title>
            </Helmet>
            <Main.AuthenticatedRoute component={DeliveryEventLog} />
          </>
        }
      />
      <Route
        path={ROUTES.DELIVERY_TABLE}
        element={
          <>
            <Helmet defer={false}>
              <title>Delivery Records</title>
            </Helmet>
            <Main.AuthenticatedRoute component={Delivery} />
          </>
        }
      />
      <Route
        path={ROUTES.DELIVERY_RECORD_DETAILS}
        element={
          <>
            <Helmet defer={false}>
              <title>Delivery Record</title>
            </Helmet>
            <Main.AuthenticatedRoute component={DeliveryDetails} />
          </>
        }
      />
      <Route
        path={ROUTES.DELIVERY_RECORD_EDIT}
        element={
          <>
            <Helmet defer={false}>
              <title>Delivery Record Edit</title>
            </Helmet>
            <Main.AuthenticatedRoute component={DeliveryEdit} />
          </>
        }
      />
      <Route
        path={ROUTES.DELIVERY_RECORD_LINE_ITEM_EDIT}
        element={
          <>
            <Helmet defer={false}>
              <title>Delivery Product Edit</title>
            </Helmet>
            <Main.AuthenticatedRoute component={DeliveryLineItemEdit} />
          </>
        }
      />

      {/* Waste */}
      <Route path={ROUTES.WASTE_ROOT} element={<Navigate to={'./table'} />} />
      <Route
        path={ROUTES.WASTE_RECORD_DETAILS}
        element={
          <>
            <Helmet defer={false}>
              <title>Waste Record</title>
            </Helmet>
            <Main.AuthenticatedRoute component={WasteDetails} />
          </>
        }
      />
      <Route
        path={ROUTES.WASTE_RECORD_EDIT}
        element={
          <>
            <Helmet defer={false}>
              <title>Waste Record Edit</title>
            </Helmet>
            <Main.AuthenticatedRoute component={WasteEdit} />
          </>
        }
      />
      <Route
        path={ROUTES.WASTE_RECORD_LINE_ITEM_EDIT}
        element={
          <>
            <Helmet defer={false}>
              <title>Waste Item Edit</title>
            </Helmet>
            <Main.AuthenticatedRoute component={WasteLineItemEdit} />
          </>
        }
      />
      <Route
        path={ROUTES.WASTE_TABLE}
        element={
          <>
            <Helmet defer={false}>
              <title>Waste Records</title>
            </Helmet>
            <Main.AuthenticatedRoute component={Waste} />
          </>
        }
      />
      <Route
        path={ROUTES.WASTE_EVENTS}
        element={
          <>
            <Helmet defer={false}>
              <title>Waste Events</title>
            </Helmet>
            <Main.AuthenticatedRoute component={WasteEventLog} />
          </>
        }
      />

      {/* Alerts */}
      <Route
        path={ROUTES.ALERTS_MATERIAL_CERTIFICATIONS_TABLE}
        element={
          <>
            <Helmet defer={false}>
              <title>{ALERT_TYPES.MATERIAL_CERTIFICATIONS} Alerts</title>
            </Helmet>
            <Main.AuthenticatedRoute
              component={AlertsPage}
              alertsType={ALERT_TYPES.MATERIAL_CERTIFICATIONS}
            />
          </>
        }
      />
      <Route
        path={ROUTES.ALERTS_QUALITY_ASSURANCE_TABLE}
        element={
          <>
            <Helmet defer={false}>
              <title>{ALERT_TYPES.QUALITY_ASSURANCE} Alerts</title>
            </Helmet>
            <Main.AuthenticatedRoute
              component={AlertsPage}
              alertsType={ALERT_TYPES.QUALITY_ASSURANCE}
            />
          </>
        }
      />
      <Route
        path={ROUTES.ALERTS_SUBMISSION_ISSUES_TABLE}
        element={
          <>
            <Helmet defer={false}>
              <title>{ALERT_TYPES.SUBMISSION_ISSUES} Alerts</title>
            </Helmet>
            <Main.AuthenticatedRoute
              component={AlertsPage}
              alertsType={ALERT_TYPES.SUBMISSION_ISSUES}
            />
          </>
        }
      />
      <Route
        path={ROUTES.ALERTS_WASTE_COMPLIANCE_TABLE}
        element={
          <>
            <Helmet defer={false}>
              <title>{ALERT_TYPES.WASTE_COMPLIANCE} Alerts</title>
            </Helmet>
            <Main.AuthenticatedRoute
              component={AlertsPage}
              alertsType={ALERT_TYPES.WASTE_COMPLIANCE}
            />
          </>
        }
      />

      {/* UTILITIES */}
      <Route
        path={ROUTES.UTILITIES_METERS}
        element={
          <>
            <Helmet defer={false}>
              <title>Utilities Meters</title>
            </Helmet>
            <Main.AuthenticatedRoute component={MetersPage} />
          </>
        }
      />
      <Route
        path={ROUTES.UTILITIES_READINGS}
        element={
          <>
            <Helmet defer={false}>
              <title>Meter Readings</title>
            </Helmet>
            <Main.AuthenticatedRoute component={ReadingsPage} />
          </>
        }
      />

      {/* PROJECT SETTINGS */}
      <Route
        path={ROUTES.PROJECTSETTINGS}
        element={
          <>
            <Helmet defer={false}>
              <title>Project Settings</title>
            </Helmet>
            <Main.AuthenticatedRoute component={ProjectSettings} />
          </>
        }
      />

      {/* Account Navigation */}
      <Route
        path={ROUTES.ACCOUNTS}
        element={
          <>
            <Helmet defer={false}>
              <title>Accounts</title>
            </Helmet>
            <Main.AuthenticatedRoute component={Accounts} />
          </>
        }
      />

      {/*BI Reports*/}
      <Route
        path={ROUTES.BI_REPORT}
        element={
          <>
            <Helmet defer={false}>
              <title>Report</title>
            </Helmet>
            <Main.AuthenticatedRoute component={BIReport} />
          </>
        }
      />

      {/* Settings Navigation */}
      <Route
        path={ROUTES.ACCOUNT_SETTINGS}
        element={
          <>
            <Helmet defer={false}>
              <title>Account Settings</title>
            </Helmet>
            <Main.AuthenticatedRoute component={AccountSettings} />
          </>
        }
      />

      {/* Assignee No Records Assigned */}
      <Route
        path={ROUTES.ASSIGNEE_NO_RECORDS_LANDING}
        element={
          <>
            <Helmet defer={false}>
              <title>No Records Assigned</title>
            </Helmet>
            <Main.AuthenticatedRoute
              component={AssigneeNoRecordsAssignedPage}
            />
          </>
        }
      />

      <Route
        path="/"
        element={
          <Main.UnauthenticatedRoute
            component={Login}
            redirectOnAuthenticated
          />
        }
      />
      <Route
        path={ROUTES.NOT_FOUND}
        element={<Main.UnauthenticatedRoute component={NotFound} />}
      />
      <Route
        path={ROUTES.FORBIDDEN}
        element={<Main.UnauthenticatedRoute component={Forbidden} />}
      />
      <Route
        path="*"
        element={<Main.UnauthenticatedRoute component={NotFound} />}
      />
    </Routes>
  )
}
